/**
 * useTaskLayerInteractions Hook
 *
 * Handles all user interactions on the Symbol Layer markers and clusters:
 * - Desktop: click (delayed), double-click, right-click context menu, hover
 * - Mobile: tap (single-tap via delayed click), double-tap, long-press context menu
 * - Cluster: click to expand/zoom
 *
 * Uses native MapLibre event listeners (not React event props) for Symbol Layer
 * features, which are GPU-rendered and have no DOM elements to attach to.
 *
 * @module components/map/UnifiedAnchorSymbolLayer/hooks/useTaskLayerInteractions
 */

import { useEffect, useCallback, useRef, type RefObject } from 'react';
import type { MapRef } from '@vis.gl/react-maplibre';
import type { MapLayerMouseEvent, MapLayerTouchEvent } from 'maplibre-gl';
import maplibregl from 'maplibre-gl';
import { useGeoMarkupStore } from '@/store';
import {
  selectIsInteractionSuppressed,
  selectIsReadOnlyViewer,
} from '@/store/selectors';
import { CURSOR_POINTER, CURSOR_DEFAULT } from '@/constants/geometry';
import { useTaskNavigation } from '@/hooks/useTaskNavigation';
import { useTaskOperations } from '@/hooks/useTaskOperations';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import { useDelayedClick } from '@/hooks/gestures';
import {
  createDoubleTapZoomProtection,
  type DoubleTapZoomProtection,
} from '@/utils/mapInteractionHelpers';
import {
  INTERACTION_SINGLE_CLICK_DELAY,
  INTERACTION_LONG_PRESS_DURATION,
  INTERACTION_DOUBLE_TAP_THRESHOLD,
  INTERACTION_TAP_MAX_DURATION,
} from '@/constants';
import { LAYER_IDS } from '../styles/symbolLayerStyles';
import { MAP_CLUSTER_SOURCE_ID } from '../../MapClusterLayer/featureKind';
import type { Coordinates } from '@/types';

// ===== Cluster zoom tuning =====

/** Padding (px) around the cluster's child bounding box when fitting. */
const CLUSTER_FIT_BOUNDS_PADDING = 80;
/** easeTo duration when fitting to a cluster's children. */
const CLUSTER_FIT_BOUNDS_DURATION_MS = 600;
/**
 * Buffer added on top of `getClusterExpansionZoom` to guarantee the cluster
 * actually splits on tight bounding boxes (sub-meter point spread).
 */
const CLUSTER_MAX_ZOOM_BUFFER = 1;

// ===== Types =====

interface UseTaskLayerInteractionsProps {
  /** React ref to the MapLibre map component */
  mapRef: RefObject<MapRef | null>;
  /** Whether the map has finished initial loading */
  isMapReady: boolean;
}

// ===== Helper: Extract task data from feature =====

interface FeatureTaskData {
  taskId: string;
  shortId: string;
  coordinates: Coordinates;
}

/**
 * Extracts task ID, shortId, and coordinates from a MapLibre layer event feature.
 * Returns null if the event has no features or missing required properties.
 */
function extractTaskData(
  event: MapLayerMouseEvent | MapLayerTouchEvent
): FeatureTaskData | null {
  const feature = event.features?.[0];
  if (!feature) return null;

  const taskId = feature.properties?.id as string | undefined;
  if (!taskId) return null;

  const shortId = (feature.properties?.shortId as string | undefined) ?? taskId;

  // Use feature geometry (anchor point) — NOT event.lngLat (click position on marker edge)
  const geom = feature.geometry;
  const coordinates: Coordinates =
    geom.type === 'Point' && geom.coordinates.length >= 2
      ? {
          lng: geom.coordinates[0] as number,
          lat: geom.coordinates[1] as number,
        }
      : { lat: event.lngLat.lat, lng: event.lngLat.lng };

  return { taskId, shortId, coordinates };
}

// ===== Sub-hooks =====

/**
 * Manages desktop click interactions (single-click with delay, double-click, context menu).
 * Single-click navigates to task details after a delay to distinguish from double-click.
 * Double-click opens edit mode and cancels the pending single-click.
 */
function useDesktopClickInteractions(
  mapRef: RefObject<MapRef | null>,
  isMapReady: boolean,
  isDesktop: boolean
): void {
  const { navigateToTaskDetails } = useTaskNavigation();
  const { openTaskForEdit } = useTaskOperations();
  const showContextMenu = useGeoMarkupStore(
    (state) => state.ui.showContextMenu
  );

  // Delayed click for single-click vs double-click disambiguation
  const pendingTaskDataRef = useRef<FeatureTaskData | null>(null);

  const { scheduleClick, cancelPendingClick } = useDelayedClick({
    delay: INTERACTION_SINGLE_CLICK_DELAY,
    onClick: () => {
      const taskData = pendingTaskDataRef.current;
      if (taskData) {
        navigateToTaskDetails(taskData.shortId, taskData.coordinates);
        pendingTaskDataRef.current = null;
      }
    },
  });

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !isMapReady || !isDesktop) return;

    /**
     * Handle single click on marker shape layer (desktop).
     * Schedules a delayed navigation; cancelled if double-click fires within delay.
     */
    const handleMarkerClick = (event: MapLayerMouseEvent): void => {
      // Skip if interaction is suppressed (panel just closed via click-outside)
      if (selectIsInteractionSuppressed(useGeoMarkupStore.getState())) return;

      const taskData = extractTaskData(event);
      if (!taskData) return;

      // Store task data for the delayed click callback
      pendingTaskDataRef.current = taskData;
      scheduleClick();
    };

    /**
     * Handle double-click on marker shape layer (desktop).
     * Opens edit form and cancels any pending single-click.
     */
    const handleMarkerDblClick = (event: MapLayerMouseEvent): void => {
      // Prevent map zoom on double-click
      event.preventDefault();

      // Read-only viewers cannot edit — keep the pending single-click alive so
      // a fast double-click still opens the read-only detail panel.
      if (selectIsReadOnlyViewer(useGeoMarkupStore.getState())) return;

      // Cancel pending single-click
      cancelPendingClick();
      pendingTaskDataRef.current = null;

      const taskData = extractTaskData(event);
      if (!taskData) return;

      openTaskForEdit(taskData.shortId, taskData.coordinates);
    };

    /**
     * Handle right-click context menu on marker shape layer (desktop).
     * Shows task context menu via Zustand unified state.
     */
    const handleMarkerContextMenu = (event: MapLayerMouseEvent): void => {
      event.preventDefault();

      // No context menu (edit/delete actions) for read-only viewers.
      if (selectIsReadOnlyViewer(useGeoMarkupStore.getState())) return;

      const taskData = extractTaskData(event);
      if (!taskData) return;

      const position = { x: event.point.x, y: event.point.y };
      showContextMenu('task', position, {
        taskId: taskData.shortId,
        geometryId: taskData.taskId,
        coordinates: taskData.coordinates,
      });
    };

    // Register desktop event listeners
    map.on('click', LAYER_IDS.MARKER_COMPOSITE, handleMarkerClick);
    map.on('dblclick', LAYER_IDS.MARKER_COMPOSITE, handleMarkerDblClick);
    map.on('contextmenu', LAYER_IDS.MARKER_COMPOSITE, handleMarkerContextMenu);

    return () => {
      map.off('click', LAYER_IDS.MARKER_COMPOSITE, handleMarkerClick);
      map.off('dblclick', LAYER_IDS.MARKER_COMPOSITE, handleMarkerDblClick);
      map.off(
        'contextmenu',
        LAYER_IDS.MARKER_COMPOSITE,
        handleMarkerContextMenu
      );
      cancelPendingClick();
    };
  }, [
    mapRef,
    isMapReady,
    isDesktop,
    scheduleClick,
    cancelPendingClick,
    navigateToTaskDetails,
    openTaskForEdit,
    showContextMenu,
  ]);
}

/**
 * Manages mobile touch interactions (single-tap, double-tap, long-press).
 * Single-tap navigates to task details, double-tap opens edit,
 * long-press shows context menu.
 */
function useMobileTouchInteractions(
  mapRef: RefObject<MapRef | null>,
  isMapReady: boolean,
  isTouch: boolean
): void {
  const { navigateToTaskDetails } = useTaskNavigation();
  const { openTaskForEdit } = useTaskOperations();
  const showContextMenu = useGeoMarkupStore(
    (state) => state.ui.showContextMenu
  );

  // Refs for touch gesture detection
  const lastTapTimeRef = useRef<number>(0);
  const touchStartTimeRef = useRef<number>(0);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);
  const touchStartDataRef = useRef<FeatureTaskData | null>(null);
  const doubleTapProtectionRef = useRef<DoubleTapZoomProtection | null>(null);

  /** Clear any pending long-press timer */
  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !isMapReady || !isTouch) return;

    /**
     * Handle touch start on marker shape layer (mobile).
     * Starts long-press timer and records touch start time.
     */
    const handleTouchStart = (event: MapLayerTouchEvent): void => {
      const touch = event.originalEvent.touches[0];
      if (!touch) return;

      longPressTriggeredRef.current = false;
      touchStartTimeRef.current = Date.now();

      const taskData = extractTaskData(event);
      touchStartDataRef.current = taskData;

      // Protect against double-tap zoom
      if (!doubleTapProtectionRef.current) {
        doubleTapProtectionRef.current = createDoubleTapZoomProtection(map);
      }
      doubleTapProtectionRef.current.protect();

      // Start long-press timer
      clearLongPressTimer();
      const position = { x: touch.clientX, y: touch.clientY };

      longPressTimerRef.current = setTimeout(() => {
        // Skip if interactions are suppressed or the viewer is read-only
        // (long-press opens the edit/delete context menu).
        const storeState = useGeoMarkupStore.getState();
        if (
          selectIsInteractionSuppressed(storeState) ||
          selectIsReadOnlyViewer(storeState)
        ) {
          longPressTimerRef.current = null;
          return;
        }

        if (touchStartDataRef.current) {
          longPressTriggeredRef.current = true;

          showContextMenu('task', position, {
            taskId: touchStartDataRef.current.shortId,
            geometryId: touchStartDataRef.current.taskId,
            coordinates: touchStartDataRef.current.coordinates,
          });
        }
      }, INTERACTION_LONG_PRESS_DURATION);
    };

    /**
     * Handle touch end on marker shape layer (mobile).
     * Detects single-tap or double-tap after filtering out long-press.
     */
    const handleTouchEnd = (event: MapLayerTouchEvent): void => {
      clearLongPressTimer();

      // Skip if long-press was triggered
      if (longPressTriggeredRef.current) {
        longPressTriggeredRef.current = false;
        return;
      }

      // Check if this was a tap (short touch) vs long-press
      const touchDuration = Date.now() - touchStartTimeRef.current;
      if (touchDuration > INTERACTION_TAP_MAX_DURATION) return;

      const taskData = touchStartDataRef.current;
      if (!taskData) return;

      const now = Date.now();
      const timeSinceLastTap = now - lastTapTimeRef.current;

      if (
        timeSinceLastTap < INTERACTION_DOUBLE_TAP_THRESHOLD &&
        timeSinceLastTap > 0
      ) {
        // Double-tap detected: open edit mode (no-op for read-only viewers —
        // the first tap already opened the read-only detail panel)
        event.preventDefault();
        lastTapTimeRef.current = 0;
        if (!selectIsReadOnlyViewer(useGeoMarkupStore.getState())) {
          openTaskForEdit(taskData.shortId, taskData.coordinates);
        }
      } else {
        // Single tap: navigate to task details
        lastTapTimeRef.current = now;
        navigateToTaskDetails(taskData.shortId, taskData.coordinates);
      }
    };

    /**
     * Handle touch cancel (clears long-press timer)
     */
    const handleTouchCancel = (): void => {
      clearLongPressTimer();
      longPressTriggeredRef.current = false;
      touchStartDataRef.current = null;
    };

    // Register mobile event listeners
    map.on('touchstart', LAYER_IDS.MARKER_COMPOSITE, handleTouchStart);
    map.on('touchend', LAYER_IDS.MARKER_COMPOSITE, handleTouchEnd);
    map.on('touchcancel', LAYER_IDS.MARKER_COMPOSITE, handleTouchCancel);

    return () => {
      map.off('touchstart', LAYER_IDS.MARKER_COMPOSITE, handleTouchStart);
      map.off('touchend', LAYER_IDS.MARKER_COMPOSITE, handleTouchEnd);
      map.off('touchcancel', LAYER_IDS.MARKER_COMPOSITE, handleTouchCancel);
      clearLongPressTimer();
      doubleTapProtectionRef.current?.cleanup();
    };
  }, [
    mapRef,
    isMapReady,
    isTouch,
    clearLongPressTimer,
    navigateToTaskDetails,
    openTaskForEdit,
    showContextMenu,
  ]);
}

/**
 * Manages cursor changes on hover over a specific layer (desktop only).
 * Sets cursor to 'pointer' on mouseenter, resets on mouseleave.
 */
function useLayerHoverCursor(
  mapRef: RefObject<MapRef | null>,
  isMapReady: boolean,
  hasHover: boolean,
  layerId: string
): void {
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !isMapReady || !hasHover) return;

    const handleMouseEnter = (): void => {
      map.getCanvas().style.cursor = CURSOR_POINTER;
    };

    const handleMouseLeave = (): void => {
      map.getCanvas().style.cursor = CURSOR_DEFAULT;
    };

    map.on('mouseenter', layerId, handleMouseEnter);
    map.on('mouseleave', layerId, handleMouseLeave);

    return () => {
      map.off('mouseenter', layerId, handleMouseEnter);
      map.off('mouseleave', layerId, handleMouseLeave);
    };
  }, [mapRef, isMapReady, hasHover, layerId]);
}

/**
 * Manages cluster click interaction: zooms into the cluster to expand it.
 * Uses getClusterExpansionZoom to determine the appropriate zoom level.
 */
function useClusterClickInteraction(
  mapRef: RefObject<MapRef | null>,
  isMapReady: boolean
): void {
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !isMapReady) return;

    /**
     * Handle click on cluster circle.
     *
     * Fits the camera to the cluster's child anchors so the user always lands
     * on a frame containing the underlying tasks. Capping at
     * `expansionZoom + buffer` guarantees the cluster actually splits even on
     * very tight point spreads where `fitBounds` would otherwise overshoot.
     */
    const handleClusterClick = async (
      event: MapLayerMouseEvent
    ): Promise<void> => {
      const feature = event.features?.[0];
      if (!feature) return;

      const clusterId = feature.properties?.cluster_id as number | undefined;
      if (clusterId === undefined) return;

      const source = map.getSource(MAP_CLUSTER_SOURCE_ID) as
        | maplibregl.GeoJSONSource
        | undefined;
      if (!source) return;

      try {
        const [expansionZoom, leaves] = await Promise.all([
          source.getClusterExpansionZoom(clusterId),
          source.getClusterLeaves(clusterId, Infinity, 0),
        ]);

        if (leaves.length === 0) return;

        const bounds = new maplibregl.LngLatBounds();
        for (const leaf of leaves) {
          if (leaf.geometry.type !== 'Point') continue;
          bounds.extend(leaf.geometry.coordinates as [number, number]);
        }

        map.fitBounds(bounds, {
          padding: CLUSTER_FIT_BOUNDS_PADDING,
          maxZoom: expansionZoom + CLUSTER_MAX_ZOOM_BUFFER,
          duration: CLUSTER_FIT_BOUNDS_DURATION_MS,
        });
      } catch (error) {
        console.error('Failed to zoom to cluster:', error);
      }
    };

    map.on('click', LAYER_IDS.CLUSTER_CIRCLES, handleClusterClick);

    return () => {
      map.off('click', LAYER_IDS.CLUSTER_CIRCLES, handleClusterClick);
    };
  }, [mapRef, isMapReady]);
}

// ===== Main Hook =====

/**
 * Manages all user interactions on the Symbol Layer markers and clusters.
 *
 * Registers native MapLibre event listeners for:
 * - Desktop: click (delayed), double-click, context menu, hover
 * - Mobile: tap, double-tap, long-press
 * - Cluster: click to expand, hover cursor
 *
 * All event listeners are cleaned up on unmount or dependency changes.
 *
 * @param props - Map ref and readiness flag
 */
export function useTaskLayerInteractions({
  mapRef,
  isMapReady,
}: UseTaskLayerInteractionsProps): void {
  const { isDesktop, isTouch, hasHover } = useDeviceLayout();

  // Desktop: click, double-click, context menu
  useDesktopClickInteractions(mapRef, isMapReady, isDesktop);

  // Mobile: tap, double-tap, long-press
  useMobileTouchInteractions(mapRef, isMapReady, isTouch);

  // Marker hover cursor + preview popup are owned by `useMapMarkerHoverPreview`
  // (registered in MapInstance) — single global writer keeps overlapping
  // task/note markers from racing for the canvas cursor.

  // Cluster: click to expand
  useClusterClickInteraction(mapRef, isMapReady);

  // Cluster: hover cursor (desktop only) — the global hook only queries marker
  // layers, so cluster hover stays a per-layer concern here.
  useLayerHoverCursor(mapRef, isMapReady, hasHover, LAYER_IDS.CLUSTER_CIRCLES);
}
